import type {
  AnalyticsInterval,
  AnalyticsMetricName,
  AnalyticsSplitName,
  StaffAnalyticsMetric,
} from "@lafayette/schemas";

import { REQUEST_TYPE_LABELS } from "../overview-model";

// The words of the Explore screen for each metric and split. The server owns the closed lists (`packages/domain/src/customer-analytics-metrics.ts`);
// a test holds this catalog to them. Everything here is a fixed word: a chart never shows text from the API.

type Unit = StaffAnalyticsMetric["unit"];

export type MetricInfo = Readonly<{
  /** The name in the "Show" list. */
  label: string;
  /** The title of the chart for a bucket of `interval`: "Sessions per day". */
  title: (interval: AnalyticsInterval) => string;
  unit: Unit;
  /** The splits the server allows for this metric, in the order of the "Split by" list. */
  splits: readonly AnalyticsSplitName[];
  /** `true`: read from the visits of the phone app, so consent applies. */
  app: boolean;
  /** The word for one of it, and for many ("session", "sessions"), in a sentence. */
  noun: readonly [string, string];
}>;

const PER: Readonly<Record<AnalyticsInterval, string>> = {
  hour: "per hour",
  day: "per day",
  week: "per week",
};

const per = (name: string) => (interval: AnalyticsInterval) => `${name} ${PER[interval]}`;

export const METRIC_INFO: Readonly<Record<AnalyticsMetricName, MetricInfo>> = {
  sessions: {
    label: "Sessions",
    title: per("Sessions"),
    unit: "count",
    splits: ["none", "platform", "entry"],
    app: true,
    noun: ["session", "sessions"],
  },
  time_in_app: {
    label: "Time in app",
    title: per("Time in the app"),
    unit: "seconds",
    splits: ["none", "platform", "entry"],
    app: true,
    noun: ["second", "seconds"],
  },
  screens_per_session: {
    label: "Screens per session",
    title: per("Screens per session"),
    unit: "ratio",
    splits: ["none", "platform"],
    app: true,
    noun: ["screen", "screens"],
  },
  screen_time: {
    label: "Time by screen",
    title: per("Time on screens"),
    unit: "seconds",
    splits: ["none", "screen"],
    app: true,
    noun: ["second", "seconds"],
  },
  active_days: {
    label: "Active days",
    title: per("Active days"),
    unit: "count",
    splits: ["none"],
    app: true,
    noun: ["day", "days"],
  },
  requests_created: {
    label: "Requests made",
    title: per("Requests made"),
    unit: "count",
    splits: ["none", "request_type"],
    app: false,
    noun: ["request", "requests"],
  },
  requests_completed: {
    label: "Requests completed",
    title: per("Requests completed"),
    unit: "count",
    splits: ["none", "request_type"],
    app: false,
    noun: ["request", "requests"],
  },
  payments_paid: {
    label: "Payments",
    title: per("Payments"),
    unit: "count",
    splits: ["none"],
    app: false,
    noun: ["payment", "payments"],
  },
  amount_paid: {
    label: "Amount paid",
    title: per("Amount paid"),
    unit: "minor_inr",
    splits: ["none"],
    app: false,
    noun: ["payment", "payments"],
  },
  trips_created: {
    label: "Trips made",
    title: per("Trips made"),
    unit: "count",
    splits: ["none"],
    app: false,
    noun: ["trip", "trips"],
  },
  saved_items_added: {
    label: "Saved items",
    title: per("Saved items"),
    unit: "count",
    splits: ["none"],
    app: false,
    noun: ["saved item", "saved items"],
  },
  handoffs_requested: {
    label: "Handoffs",
    title: per("Handoffs"),
    unit: "count",
    splits: ["none"],
    app: false,
    noun: ["handoff", "handoffs"],
  },
  customer_messages: {
    label: "Messages",
    title: per("Messages from the customer"),
    unit: "count",
    splits: ["none"],
    app: false,
    noun: ["message", "messages"],
  },
  notifications_sent: {
    label: "Notifications sent",
    title: per("Notifications sent"),
    unit: "count",
    splits: ["none"],
    app: false,
    noun: ["notification", "notifications"],
  },
};

export const METRIC_NAMES = Object.keys(METRIC_INFO) as readonly AnalyticsMetricName[];

export const SPLIT_LABELS: Readonly<Record<AnalyticsSplitName, string>> = {
  none: "No split",
  platform: "Device",
  entry: "Came from",
  screen: "Screen",
  request_type: "Request type",
};

const SCREEN_LABELS: Readonly<Record<string, string>> = {
  concierge: "Chat",
  trips: "Trips",
  trip_detail: "Trip details",
  trip_new: "New trip",
  trip_edit: "Edit trip",
  itinerary_add: "Add to plan",
  itinerary_review: "Review plan",
  itinerary_edit: "Edit plan",
  actions: "Actions",
  action_detail: "Action details",
  saved: "Saved",
  saved_item: "Saved item",
  profile: "Profile",
  memory: "Memory",
  request_new: "New request",
  request_answer: "Answer a request",
  sign_in: "Sign in",
  other: "Other screens",
};

export const ENTRY_LABELS: Readonly<Record<string, string>> = {
  cold_start: "Opened the app",
  resume: "Came back to the app",
  deep_link: "A link",
  notification: "A notification",
  unknown: "Unknown",
};

export const PLATFORM_LABELS: Readonly<Record<string, string>> = {
  ios: "iPhone",
  android: "Android",
  unknown: "Unknown",
};

/** The name of one series of a split: a closed key to a fixed word. `all` is the series of "No split"; `other` holds the keys past the limit. */
export function seriesLabel(split: AnalyticsSplitName, key: string): string {
  if (key === "all") return "All";
  if (key === "other" && split !== "screen") return "Other";
  const table: Readonly<Record<string, string>> =
    split === "platform"
      ? PLATFORM_LABELS
      : split === "entry"
        ? ENTRY_LABELS
        : split === "screen"
          ? SCREEN_LABELS
          : (REQUEST_TYPE_LABELS as Readonly<Record<string, string>>);
  return table[key] ?? "Other";
}

/** The name of a screen of the phone app, for "Where the time went". */
export const screenLabel = (screen: string): string => SCREEN_LABELS[screen] ?? "Other screens";
